'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, CalendarDays, FlaskConical, Heart, LogOut, Menu, X, ChevronRight, Bell, Download, CheckCircle2, AlertCircle, Clock, TrendingUp, Activity, User, Phone, Mail, MapPin, Shield, ChevronDown, ChevronUp, BellRing, Trash2, Package, ExternalLink, RefreshCw, Receipt, TestTube2, XCircle, CalendarClock, CreditCard, Banknote, Smartphone, CheckCheck,  } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { mockLabReports, healthTrends, healthMetrics } from './mockPatientData';
import { useAuth } from '@/contexts/AuthContext';
import {
  getBookingHistory,
  getFavouritePackages,
  getPriceAlerts,
  removeFavouritePackage,
  deactivatePriceAlert,
  cancelBooking,
  requestReschedule,
  getPaymentReceipts,
  getTestResults,
  type BookingRecord,
  type FavouritePackage,
  type PriceAlert,
  type PaymentReceipt,
  type TestResult,
} from '@/lib/services/patientDataService';
import { createClient } from '@/lib/supabase/client';

type Tab = 'overview' | 'appointments' | 'test-results' | 'receipts' | 'favourites' | 'alerts' | 'reports' | 'health' | 'profile';

const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <LayoutDashboard size={18} /> },
  { id: 'appointments', label: 'My Bookings', icon: <CalendarDays size={18} /> },
  { id: 'test-results', label: 'Test Results', icon: <TestTube2 size={18} /> },
  { id: 'receipts', label: 'Payment Receipts', icon: <Receipt size={18} /> },
  { id: 'favourites', label: 'Saved Packages', icon: <Heart size={18} /> },
  { id: 'alerts', label: 'Price Alerts', icon: <BellRing size={18} /> },
  { id: 'reports', label: 'Lab Reports', icon: <FlaskConical size={18} /> },
  { id: 'health', label: 'Health Summary', icon: <Activity size={18} /> },
  { id: 'profile', label: 'My Profile', icon: <User size={18} /> },
];

interface PatientUser {
  email: string;
  name: string;
  patientId: string;
  phone: string;
}

// ── Shared Components ────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    completed: 'bg-success/10 text-success',
    upcoming: 'bg-primary/10 text-primary',
    cancelled: 'bg-danger/10 text-danger',
    normal: 'bg-success/10 text-success',
    attention: 'bg-warning/10 text-warning',
    high: 'bg-danger/10 text-danger',
    low: 'bg-warning/10 text-warning',
    paid: 'bg-success/10 text-success',
    pending: 'bg-warning/10 text-warning',
    refunded: 'bg-primary/10 text-primary',
    ready: 'bg-success/10 text-success',
    processing: 'bg-primary/10 text-primary',
  };
  const labels: Record<string, string> = {
    completed: 'Completed',
    upcoming: 'Upcoming',
    cancelled: 'Cancelled',
    normal: 'Normal',
    attention: 'Needs Attention',
    high: 'High',
    low: 'Low',
    paid: 'Paid',
    pending: 'Pending',
    refunded: 'Refunded',
    ready: 'Ready',
    processing: 'Processing',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${map[status] ?? 'bg-muted text-muted-foreground'}`}>
      {labels[status] ?? status}
    </span>
  );
}

function EmptyState({ icon, title, desc, action }: { icon: React.ReactNode; title: string; desc: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center">
      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-4 opacity-50">
        {icon}
      </div>
      <p className="font-semibold text-foreground mb-1">{title}</p>
      <p className="text-sm text-muted-foreground max-w-xs">{desc}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ── Cancel Modal ─────────────────────────────────────────────

function CancelModal({
  appointmentId,
  service,
  onConfirm,
  onClose,
}: {
  appointmentId: string;
  service: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const reasons = ['Schedule conflict', 'Feeling better', 'Found another lab', 'Cost concerns', 'Other'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-2xl shadow-modal w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h3 className="font-bold text-foreground">Cancel Appointment</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{service} · {appointmentId}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <X size={18} className="text-muted-foreground" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">Please select a reason for cancellation:</p>
          <div className="space-y-2">
            {reasons.map((r) => (
              <label key={r} className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer hover:border-primary/40 hover:bg-secondary transition-colors">
                <input
                  type="radio"
                  name="cancel-reason"
                  value={r}
                  checked={reason === r}
                  onChange={() => setReason(r)}
                  className="accent-primary"
                />
                <span className="text-sm font-medium text-foreground">{r}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:bg-muted transition-colors">
              Keep Appointment
            </button>
            <button
              onClick={() => reason && onConfirm(reason)}
              disabled={!reason}
              className="flex-1 px-4 py-2.5 rounded-xl bg-danger text-white text-sm font-semibold hover:bg-danger/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel Appointment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Booking History Tab ──────────────────────────────────────

function BookingHistoryTab({
  userId,
  bookings,
  loadingBookings,
  onRefresh,
}: {
  userId: string;
  bookings: BookingRecord[];
  loadingBookings: boolean;
  onRefresh: () => void;
}) {
  const upcoming = bookings.filter((b) => b.status === 'upcoming');
  const past = bookings.filter((b) => b.status !== 'upcoming');
  const [cancelTarget, setCancelTarget] = useState<BookingRecord | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rescheduleSuccess, setRescheduleSuccess] = useState<string | null>(null);

  const handleCancel = async (reason: string) => {
    if (!cancelTarget) return;
    setActionLoading(cancelTarget.appointmentId);
    try {
      await cancelBooking(userId, cancelTarget.appointmentId, reason);
      onRefresh();
    } catch {
      // silently fail
    } finally {
      setActionLoading(null);
      setCancelTarget(null);
    }
  };

  const handleReschedule = async (appointmentId: string) => {
    setActionLoading(appointmentId);
    try {
      await requestReschedule(userId, appointmentId);
      setRescheduleSuccess(appointmentId);
      setTimeout(() => setRescheduleSuccess(null), 3000);
    } catch {
      // silently fail
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {cancelTarget && (
        <CancelModal
          appointmentId={cancelTarget.appointmentId}
          service={cancelTarget.service}
          onConfirm={handleCancel}
          onClose={() => setCancelTarget(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">My Bookings</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{bookings.length} total appointment{bookings.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRefresh} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Refresh">
            <RefreshCw size={16} className="text-muted-foreground" />
          </button>
          <Link href="/book-appointment" className="btn-primary text-sm py-2 px-4">
            <CalendarDays size={14} />
            Book New
          </Link>
        </div>
      </div>

      {loadingBookings ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : bookings.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <EmptyState
            icon={<CalendarDays size={28} className="text-muted-foreground" />}
            title="No bookings yet"
            desc="Your appointment history will appear here once you book your first test."
            action={
              <Link href="/book-appointment" className="btn-primary text-sm py-2 px-5">
                Book Appointment
              </Link>
            }
          />
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Upcoming</h3>
              <div className="space-y-3">
                {upcoming.map((b) => (
                  <div key={b.appointmentId} className="bg-card border border-primary/20 rounded-2xl p-4 shadow-card">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Clock size={18} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <div className="font-semibold text-foreground">{b.service}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{b.bookingDate} · {b.bookingTime} · {b.location}</div>
                          </div>
                          <StatusBadge status={b.status} />
                        </div>
                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          <span className="text-xs text-muted-foreground font-mono">{b.appointmentId}</span>
                          <span className="text-sm font-bold text-foreground">{b.totalAmount}</span>
                          {b.isHomeCollection && (
                            <span className="text-xs bg-accent/10 text-accent font-semibold px-2 py-0.5 rounded-full">Home Collection</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          {rescheduleSuccess === b.appointmentId ? (
                            <span className="flex items-center gap-1.5 text-xs font-semibold text-success">
                              <CheckCheck size={13} />
                              Reschedule request sent!
                            </span>
                          ) : (
                            <button
                              onClick={() => handleReschedule(b.appointmentId)}
                              disabled={actionLoading === b.appointmentId}
                              className="flex items-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 rounded-lg px-3 py-1.5 hover:bg-primary/5 transition-colors disabled:opacity-50"
                            >
                              {actionLoading === b.appointmentId ? (
                                <div className="w-3 h-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <CalendarClock size={13} />
                              )}
                              Reschedule
                            </button>
                          )}
                          <button
                            onClick={() => setCancelTarget(b)}
                            disabled={actionLoading === b.appointmentId}
                            className="flex items-center gap-1.5 text-xs font-semibold text-danger border border-danger/30 rounded-lg px-3 py-1.5 hover:bg-danger/5 transition-colors disabled:opacity-50"
                          >
                            <XCircle size={13} />
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {past.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Past Appointments</h3>
              <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-card">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40">
                        <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Appt. ID</th>
                        <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Date & Time</th>
                        <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Service</th>
                        <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground hidden md:table-cell">Location</th>
                        <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Amount</th>
                        <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {past.map((b, i) => (
                        <tr key={b.appointmentId} className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                          <td className="px-5 py-4 font-mono text-xs text-muted-foreground">{b.appointmentId}</td>
                          <td className="px-5 py-4">
                            <div className="font-medium text-foreground">{b.bookingDate}</div>
                            <div className="text-xs text-muted-foreground">{b.bookingTime}</div>
                          </td>
                          <td className="px-5 py-4 font-medium text-foreground">{b.service}</td>
                          <td className="px-5 py-4 text-muted-foreground hidden md:table-cell">{b.location}</td>
                          <td className="px-5 py-4 font-semibold text-foreground">{b.totalAmount}</td>
                          <td className="px-5 py-4">
                            <div className="flex flex-col gap-1">
                              <StatusBadge status={b.status} />
                              {b.cancelReason && (
                                <span className="text-xs text-muted-foreground">{b.cancelReason}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Test Results Tab ─────────────────────────────────────────

function TestResultsTab({
  testResults,
  loading,
  onRefresh,
}: {
  testResults: TestResult[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const statusIcon = (status: string) => {
    if (status === 'ready' || status === 'normal') return <CheckCircle2 size={16} className="text-success" />;
    if (status === 'attention' || status === 'high' || status === 'low') return <AlertCircle size={16} className="text-warning" />;
    return <Clock size={16} className="text-primary" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">Test Results</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{testResults.length} result{testResults.length !== 1 ? 's' : ''} available</p>
        </div>
        <button onClick={onRefresh} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Refresh">
          <RefreshCw size={16} className="text-muted-foreground" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : testResults.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <EmptyState
            icon={<TestTube2 size={28} className="text-muted-foreground" />}
            title="No test results yet"
            desc="Your test results will appear here once your lab reports are processed."
            action={
              <Link href="/book-appointment" className="btn-primary text-sm py-2 px-5">
                Book a Test
              </Link>
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {testResults.map((result) => (
            <div key={result.id} className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors"
                onClick={() => setExpanded(expanded === result.id ? null : result.id)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    result.resultStatus === 'ready' || result.resultStatus === 'normal' ? 'bg-success/10' :
                    result.resultStatus === 'processing'|| result.resultStatus === 'pending' ? 'bg-primary/10' : 'bg-warning/10'
                  }`}>
                    {statusIcon(result.resultStatus)}
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-foreground">{result.testName}</div>
                    <div className="text-xs text-muted-foreground">
                      {result.reportDate || 'Date pending'} · {result.category} · Appt: {result.appointmentId}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={result.resultStatus} />
                  {result.reportUrl && (
                    <a
                      href={result.reportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 transition-colors"
                    >
                      <Download size={13} />PDF
                    </a>
                  )}
                  {expanded === result.id ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
                </div>
              </button>

              {expanded === result.id && (
                <div className="border-t border-border px-5 py-4 bg-muted/20">
                  {result.parameters.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left pb-2 font-semibold text-muted-foreground">Parameter</th>
                          <th className="text-left pb-2 font-semibold text-muted-foreground">Result</th>
                          <th className="text-left pb-2 font-semibold text-muted-foreground">Reference Range</th>
                          <th className="text-left pb-2 font-semibold text-muted-foreground">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.parameters.map((param) => (
                          <tr key={param.name} className="border-b border-border/50 last:border-0">
                            <td className="py-2.5 font-medium text-foreground">{param.name}</td>
                            <td className={`py-2.5 font-semibold ${param.status !== 'normal' ? 'text-danger' : 'text-foreground'}`}>{param.value}</td>
                            <td className="py-2.5 text-muted-foreground">{param.range}</td>
                            <td className="py-2.5"><StatusBadge status={param.status} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center py-4">
                      {result.resultStatus === 'pending' || result.resultStatus === 'processing' ? (
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          Results are being processed. You will be notified when ready.
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{result.notes || 'No detailed parameters available.'}</p>
                      )}
                    </div>
                  )}
                  {result.notes && result.parameters.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">{result.notes}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Payment Receipts Tab ─────────────────────────────────────

function PaymentReceiptsTab({
  receipts,
  loading,
  onRefresh,
}: {
  receipts: PaymentReceipt[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const paymentIcon = (method: string) => {
    if (method === 'cash') return <Banknote size={16} className="text-success" />;
    if (method === 'upi' || method === 'online') return <Smartphone size={16} className="text-primary" />;
    return <CreditCard size={16} className="text-accent" />;
  };

  const totalPaid = receipts
    .filter((r) => r.paymentStatus === 'paid')
    .reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">Payment Receipts</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{receipts.length} receipt{receipts.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={onRefresh} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Refresh">
          <RefreshCw size={16} className="text-muted-foreground" />
        </button>
      </div>

      {receipts.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Receipt size={18} className="text-primary" />
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Total Amount Paid</div>
            <div className="text-xl font-bold text-foreground">₹{totalPaid.toLocaleString('en-IN')}</div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : receipts.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <EmptyState
            icon={<Receipt size={28} className="text-muted-foreground" />}
            title="No payment receipts"
            desc="Your payment receipts will appear here after completing a booking."
            action={
              <Link href="/book-appointment" className="btn-primary text-sm py-2 px-5">
                Book Appointment
              </Link>
            }
          />
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Receipt No.</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Date</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Service</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground hidden md:table-cell">Payment Method</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Amount</th>
                  <th className="text-left px-5 py-3.5 font-semibold text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map((r, i) => (
                  <tr key={r.id} className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                    <td className="px-5 py-4">
                      <div className="font-mono text-xs text-muted-foreground">{r.receiptNumber}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Appt: {r.appointmentId}</div>
                    </td>
                    <td className="px-5 py-4 font-medium text-foreground">{r.paymentDate}</td>
                    <td className="px-5 py-4 font-medium text-foreground max-w-[180px] truncate">{r.service}</td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        {paymentIcon(r.paymentMethod)}
                        <span className="text-foreground capitalize">{r.paymentMethod}</span>
                      </div>
                      {r.transactionId && (
                        <div className="text-xs text-muted-foreground font-mono mt-0.5">{r.transactionId}</div>
                      )}
                    </td>
                    <td className="px-5 py-4 font-bold text-foreground">₹{r.amount.toLocaleString('en-IN')}</td>
                    <td className="px-5 py-4"><StatusBadge status={r.paymentStatus} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Saved Favourites Tab ─────────────────────────────────────

function SavedFavouritesTab({
  userId,
  favourites,
  loading,
  onRefresh,
}: {
  userId: string;
  favourites: FavouritePackage[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const [removing, setRemoving] = useState<string | null>(null);

  const handleRemove = async (packageId: string) => {
    setRemoving(packageId);
    try {
      await removeFavouritePackage(userId, packageId);
      onRefresh();
    } catch {
      // silently fail
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">Saved Packages</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{favourites.length} saved package{favourites.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRefresh} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Refresh">
            <RefreshCw size={16} className="text-muted-foreground" />
          </button>
          <Link href="/health-packages" className="btn-primary text-sm py-2 px-4">
            <Package size={14} />
            Browse Packages
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : favourites.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <EmptyState
            icon={<Heart size={28} className="text-muted-foreground" />}
            title="No saved packages"
            desc="Tap the heart icon on any health package to save it here for quick access."
            action={
              <Link href="/health-packages" className="btn-primary text-sm py-2 px-5">
                Explore Packages
              </Link>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {favourites.map((fav) => (
            <div key={fav.id} className="bg-card border border-border rounded-2xl p-5 shadow-card hover:shadow-card-hover transition-shadow group">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="w-10 h-10 rounded-xl bg-danger/10 flex items-center justify-center flex-shrink-0">
                  <Heart size={18} className="text-danger fill-danger" />
                </div>
                <button
                  onClick={() => handleRemove(fav.packageId)}
                  disabled={removing === fav.packageId}
                  className="p-1.5 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors opacity-0 group-hover:opacity-100"
                  title="Remove from favourites"
                >
                  {removing === fav.packageId ? (
                    <div className="w-4 h-4 border-2 border-danger border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Trash2 size={15} />
                  )}
                </button>
              </div>
              <div className="font-semibold text-foreground mb-1 leading-snug">{fav.packageName}</div>
              <div className="text-xs text-muted-foreground mb-4">
                Saved {new Date(fav.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
              <div className="flex items-center gap-2">
                <Link
                  href="/health-packages"
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 rounded-lg py-2 hover:bg-primary/5 transition-colors"
                >
                  <ExternalLink size={12} />
                  View Package
                </Link>
                <Link
                  href="/book-appointment"
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-primary rounded-lg py-2 hover:bg-primary/90 transition-colors"
                >
                  <CalendarDays size={12} />
                  Book Now
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Price Alerts Tab ─────────────────────────────────────────

function PriceAlertsTab({
  userId,
  alerts,
  loading,
  onRefresh,
}: {
  userId: string;
  alerts: PriceAlert[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const [removing, setRemoving] = useState<string | null>(null);

  const handleDeactivate = async (packageId: string) => {
    setRemoving(packageId);
    try {
      await deactivatePriceAlert(userId, packageId);
      onRefresh();
    } catch {
      // silently fail
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">Active Price Alerts</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{alerts.length} active alert{alerts.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onRefresh} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Refresh">
            <RefreshCw size={16} className="text-muted-foreground" />
          </button>
          <Link href="/health-packages" className="btn-primary text-sm py-2 px-4">
            <BellRing size={14} />
            Set New Alert
          </Link>
        </div>
      </div>

      <div className="bg-warning/5 border border-warning/20 rounded-xl p-3.5 flex items-start gap-3">
        <BellRing size={16} className="text-warning flex-shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          You&apos;ll be notified via your preferred contact when a package price drops below the tracked amount.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <EmptyState
            icon={<BellRing size={28} className="text-muted-foreground" />}
            title="No active price alerts"
            desc="Set a price alert on any health package and get notified when the price drops."
            action={
              <Link href="/health-packages" className="btn-primary text-sm py-2 px-5">
                Browse Packages
              </Link>
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => (
            <div key={alert.id} className="bg-card border border-border rounded-2xl p-5 shadow-card flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center flex-shrink-0">
                <BellRing size={18} className="text-warning" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="font-semibold text-foreground">{alert.packageName}</div>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-success/10 text-success">Active</span>
                </div>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Tracked price:</span>
                    <span className="text-sm font-bold text-foreground">₹{alert.currentPrice.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-muted-foreground">Notify via:</span>
                    <span className="text-xs font-semibold text-foreground capitalize">{alert.contactType}</span>
                    <span className="text-xs text-muted-foreground truncate max-w-[140px]">({alert.contact})</span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-1.5">
                  Set on {new Date(alert.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </div>
              <button
                onClick={() => handleDeactivate(alert.packageId)}
                disabled={removing === alert.packageId}
                className="p-2 rounded-lg hover:bg-danger/10 text-muted-foreground hover:text-danger transition-colors flex-shrink-0"
                title="Deactivate alert"
              >
                {removing === alert.packageId ? (
                  <div className="w-4 h-4 border-2 border-danger border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Lab Reports Tab ──────────────────────────────────────────

function LabReportsTab() {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-foreground">Lab Reports</h2>
      <div className="space-y-3">
        {mockLabReports.map((report) => (
          <div key={report.id} className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
            <button
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors"
              onClick={() => setExpanded(expanded === report.id ? null : report.id)}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${report.status === 'normal' ? 'bg-success/10' : 'bg-warning/10'}`}>
                  <FlaskConical size={18} className={report.status === 'normal' ? 'text-success' : 'text-warning'} />
                </div>
                <div className="text-left">
                  <div className="font-semibold text-foreground">{report.testName}</div>
                  <div className="text-xs text-muted-foreground">{report.date} · {report.category}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={report.status} />
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80 transition-colors mr-2">
                  <Download size={13} />PDF
                </span>
                {expanded === report.id ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
              </div>
            </button>
            {expanded === report.id && (
              <div className="border-t border-border px-5 py-4 bg-muted/20">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left pb-2 font-semibold text-muted-foreground">Parameter</th>
                      <th className="text-left pb-2 font-semibold text-muted-foreground">Result</th>
                      <th className="text-left pb-2 font-semibold text-muted-foreground">Reference Range</th>
                      <th className="text-left pb-2 font-semibold text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.parameters.map((param) => (
                      <tr key={param.name} className="border-b border-border/50 last:border-0">
                        <td className="py-2.5 font-medium text-foreground">{param.name}</td>
                        <td className={`py-2.5 font-semibold ${param.status !== 'normal' ? 'text-danger' : 'text-foreground'}`}>{param.value}</td>
                        <td className="py-2.5 text-muted-foreground">{param.range}</td>
                        <td className="py-2.5"><StatusBadge status={param.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Health Summary Tab ───────────────────────────────────────

function HealthSummaryTab() {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-foreground">Health Summary</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Overall Health Score', value: `${healthMetrics.overallScore}%`, icon: <Heart size={20} />, color: 'text-success', bg: 'bg-success/10', note: 'Good' },
          { label: 'BMI', value: healthMetrics.bmi, icon: <Activity size={20} />, color: 'text-primary', bg: 'bg-primary/10', note: 'Normal weight' },
          { label: 'Blood Pressure', value: healthMetrics.bloodPressure, icon: <TrendingUp size={20} />, color: 'text-accent', bg: 'bg-accent/10', note: 'Optimal' },
          { label: 'Tests Completed', value: healthMetrics.testsCompleted, icon: <FlaskConical size={20} />, color: 'text-warning', bg: 'bg-warning/10', note: `${healthMetrics.attentionItems} needs attention` },
        ].map((m) => (
          <div key={m.label} className="bg-card border border-border rounded-2xl p-5 shadow-card">
            <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${m.bg} ${m.color} mb-3`}>{m.icon}</div>
            <div className="text-2xl font-bold text-foreground stats-number">{m.value}</div>
            <div className="text-sm text-muted-foreground mt-0.5">{m.label}</div>
            <div className={`text-xs font-semibold mt-2 ${m.color}`}>{m.note}</div>
          </div>
        ))}
      </div>
      <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
        <h3 className="font-semibold text-foreground mb-1">Health Trends (2025)</h3>
        <p className="text-xs text-muted-foreground mb-5">Key biomarker trends over the past 7 months</p>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={healthTrends} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }} />
            <YAxis tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }} />
            <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="glucose" name="Blood Glucose" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="cholesterol" name="Cholesterol" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <CalendarDays size={18} className="text-primary" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-foreground">Next Health Checkup Due</div>
          <div className="text-sm text-muted-foreground mt-0.5">Your next annual checkup is recommended by {healthMetrics.nextDue}</div>
        </div>
        <Link href="/book-appointment" className="btn-primary text-sm py-2 px-4 flex-shrink-0">Book Now</Link>
      </div>
    </div>
  );
}

// ── Profile Tab ──────────────────────────────────────────────

function ProfileTab({ user, onProfileUpdate }: { user: PatientUser; onProfileUpdate: (name: string, phone: string) => void }) {
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone);
  const [email] = useState(user.email);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const supabase = createClient();
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser?.id) {
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ full_name: name, phone })
          .eq('id', authUser.id);
        if (updateError) throw updateError;
      }
      onProfileUpdate(name, phone);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <h2 className="text-xl font-bold text-foreground">My Profile</h2>

      <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {user.name[0]?.toUpperCase()}
          </div>
          <div>
            <div className="font-bold text-lg text-foreground">{user.name}</div>
            <div className="text-sm text-muted-foreground">{user.email}</div>
            <div className="inline-flex items-center gap-1.5 mt-1.5 text-xs font-semibold text-primary bg-primary/10 px-2.5 py-1 rounded-full">
              <Shield size={11} />
              {user.patientId}
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Full Name</label>
              <div className="relative">
                <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="input-field pl-9" placeholder="Your full name" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number</label>
              <div className="relative">
                <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="input-field pl-9" placeholder="+91 XXXXX XXXXX" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
              <div className="relative">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="email" value={email} disabled className="input-field pl-9 opacity-60 cursor-not-allowed" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Patient ID</label>
              <div className="relative">
                <Shield size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="text" value={user.patientId} disabled className="input-field pl-9 opacity-60 cursor-not-allowed" />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Auto-assigned, cannot be changed</p>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2.5">
              <AlertCircle size={15} />
              {error}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2.5 px-6 disabled:opacity-60">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-success font-semibold">
                <CheckCircle2 size={15} />
                Changes saved!
              </span>
            )}
          </div>
        </form>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
        <h3 className="font-semibold text-foreground mb-4">Preferred Branch</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {['Sector 18', 'Sector 62', 'Sector 27'].map((branch) => (
            <label key={branch} className="flex items-center gap-3 p-3 rounded-xl border border-border cursor-pointer hover:border-primary/40 hover:bg-secondary transition-colors">
              <input type="radio" name="branch" defaultChecked={branch === 'Sector 62'} className="accent-primary" />
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <MapPin size={14} className="text-primary" />{branch}
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
        <h3 className="font-semibold text-foreground mb-4">Notification Preferences</h3>
        <div className="space-y-3">
          {[
            { label: 'Appointment reminders', desc: 'Get notified 24 hours before your appointment', checked: true },
            { label: 'Report ready alerts', desc: 'Receive an alert when your lab report is available', checked: true },
            { label: 'Health tips & offers', desc: 'Periodic health tips and promotional offers', checked: false },
          ].map((pref) => (
            <label key={pref.label} className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" defaultChecked={pref.checked} className="mt-0.5 accent-primary" />
              <div>
                <div className="text-sm font-medium text-foreground">{pref.label}</div>
                <div className="text-xs text-muted-foreground">{pref.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Dashboard ───────────────────────────────────────────

export default function PatientDashboard() {
  const router = useRouter();
  const { user, loading: authLoading, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [patientUser, setPatientUser] = useState<PatientUser | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [bookings, setBookings] = useState<BookingRecord[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  const [favourites, setFavourites] = useState<FavouritePackage[]>([]);
  const [loadingFavourites, setLoadingFavourites] = useState(false);

  const [priceAlerts, setPriceAlerts] = useState<PriceAlert[]>([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  const [paymentReceipts, setPaymentReceipts] = useState<PaymentReceipt[]>([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);

  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [loadingTestResults, setLoadingTestResults] = useState(false);

  // Load user profile
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      router.push('/patient-login');
      return;
    }
    const supabase = createClient();
    supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setPatientUser({
            email: data.email || user.email || '',
            name: data.full_name || user.email?.split('@')[0] || 'Patient',
            patientId: data.patient_id || 'PAT-00000',
            phone: data.phone || '',
          });
        } else {
          setPatientUser({
            email: user.email || '',
            name: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Patient',
            patientId: 'PAT-00000',
            phone: '',
          });
        }
      });
  }, [user, authLoading, router]);

  const loadBookings = useCallback(() => {
    if (!user?.id) return;
    setLoadingBookings(true);
    getBookingHistory(user.id).then(setBookings).finally(() => setLoadingBookings(false));
  }, [user?.id]);

  const loadFavourites = useCallback(() => {
    if (!user?.id) return;
    setLoadingFavourites(true);
    getFavouritePackages(user.id).then(setFavourites).finally(() => setLoadingFavourites(false));
  }, [user?.id]);

  const loadAlerts = useCallback(() => {
    if (!user?.id) return;
    setLoadingAlerts(true);
    getPriceAlerts(user.id).then(setPriceAlerts).finally(() => setLoadingAlerts(false));
  }, [user?.id]);

  const loadReceipts = useCallback(() => {
    if (!user?.id) return;
    setLoadingReceipts(true);
    getPaymentReceipts(user.id).then(setPaymentReceipts).finally(() => setLoadingReceipts(false));
  }, [user?.id]);

  const loadTestResults = useCallback(() => {
    if (!user?.id) return;
    setLoadingTestResults(true);
    getTestResults(user.id).then(setTestResults).finally(() => setLoadingTestResults(false));
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadBookings();
      loadFavourites();
      loadAlerts();
      loadReceipts();
      loadTestResults();
    }
  }, [user?.id, loadBookings, loadFavourites, loadAlerts, loadReceipts, loadTestResults]);

  const handleLogout = async () => {
    await signOut();
    router.push('/patient-login');
  };

  const handleProfileUpdate = (name: string, phone: string) => {
    setPatientUser((prev) => prev ? { ...prev, name, phone } : prev);
  };

  if (authLoading || !patientUser) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const completedBookings = bookings.filter((b) => b.status === 'completed').length;
  const upcomingBookings = bookings.filter((b) => b.status === 'upcoming').length;
  const readyResults = testResults.filter((r) => r.resultStatus === 'ready').length;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:static lg:flex`}
      >
        <div className="p-5 border-b border-border">
          <Link href="/" className="flex items-center gap-2.5">
            <AppLogo size={40} />
            <div>
              <div className="font-extrabold text-base text-foreground leading-tight">51 Plus Lab</div>
              <div className="text-xs text-muted-foreground">Patient Portal</div>
            </div>
          </Link>
        </div>
        <div className="px-4 py-3 mx-3 mt-3 bg-secondary rounded-xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0 text-white text-sm font-bold">
              {patientUser.name[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-bold text-foreground truncate">{patientUser.name}</div>
              <div className="text-xs text-muted-foreground">{patientUser.patientId}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                activeTab === tab.id ? 'bg-primary text-white shadow-sm' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {tab.icon}
              {tab.label}
              {tab.id === 'favourites' && favourites.length > 0 && (
                <span className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-danger/10 text-danger'}`}>
                  {favourites.length}
                </span>
              )}
              {tab.id === 'alerts' && priceAlerts.length > 0 && (
                <span className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-warning/10 text-warning'}`}>
                  {priceAlerts.length}
                </span>
              )}
              {tab.id === 'test-results' && readyResults > 0 && (
                <span className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-success/10 text-success'}`}>
                  {readyResults}
                </span>
              )}
              {tab.id === 'receipts' && paymentReceipts.length > 0 && (
                <span className={`ml-auto text-xs font-bold px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'}`}>
                  {paymentReceipts.length}
                </span>
              )}
              {activeTab === tab.id && !['favourites', 'alerts', 'test-results', 'receipts'].includes(tab.id) && (
                <ChevronRight size={14} className="ml-auto" />
              )}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-danger/10 hover:text-danger transition-colors"
          >
            <LogOut size={16} />Sign Out
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-card border-b border-border px-4 lg:px-6 h-16 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div>
              <h1 className="font-bold text-foreground text-base leading-tight">
                {tabs.find((t) => t.id === activeTab)?.label}
              </h1>
              <p className="text-xs text-muted-foreground hidden sm:block">
                {patientUser.name} · {patientUser.patientId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setActiveTab('alerts')}
              className="relative p-2 rounded-lg hover:bg-muted transition-colors"
              title="Price Alerts"
            >
              <Bell size={18} className="text-muted-foreground" />
              {priceAlerts.length > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-warning text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {priceAlerts.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold hover:bg-primary/90 transition-colors"
              title="My Profile"
            >
              {patientUser.name[0]?.toUpperCase()}
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Hello, {patientUser.name.split(' ')[0]} 👋</h2>
                <p className="text-muted-foreground mt-1">Here&apos;s a summary of your health records and activity</p>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'My Bookings', value: completedBookings, icon: <CalendarDays size={20} />, color: 'text-primary', bg: 'bg-primary/10', note: `${upcomingBookings} upcoming`, tab: 'appointments' as Tab },
                  { label: 'Test Results', value: testResults.length, icon: <TestTube2 size={20} />, color: 'text-success', bg: 'bg-success/10', note: `${readyResults} ready`, tab: 'test-results' as Tab },
                  { label: 'Payment Receipts', value: paymentReceipts.length, icon: <Receipt size={20} />, color: 'text-accent', bg: 'bg-accent/10', note: 'View all receipts', tab: 'receipts' as Tab },
                  { label: 'Health Score', value: `${healthMetrics.overallScore}%`, icon: <Activity size={20} />, color: 'text-warning', bg: 'bg-warning/10', note: 'Good overall', tab: 'health' as Tab },
                ].map((kpi) => (
                  <button
                    key={kpi.label}
                    onClick={() => setActiveTab(kpi.tab)}
                    className="bg-card border border-border rounded-2xl p-5 shadow-card hover:shadow-card-hover transition-all text-left group"
                  >
                    <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl ${kpi.bg} ${kpi.color} mb-3 group-hover:scale-105 transition-transform`}>{kpi.icon}</div>
                    <div className="text-2xl font-bold text-foreground stats-number">{kpi.value}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{kpi.label}</div>
                    <div className={`text-xs font-semibold mt-2 ${kpi.color}`}>{kpi.note}</div>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Recent Appointments */}
                <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5 shadow-card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-foreground">Recent Bookings</h3>
                    <button onClick={() => setActiveTab('appointments')} className="text-xs text-primary font-semibold hover:text-primary/80 transition-colors flex items-center gap-1">
                      View all <ChevronRight size={12} />
                    </button>
                  </div>
                  {loadingBookings ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : bookings.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-sm text-muted-foreground">No appointments yet.</p>
                      <Link href="/book-appointment" className="text-sm text-primary font-semibold hover:underline mt-1 inline-block">Book your first appointment →</Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {bookings.slice(0, 3).map((b) => (
                        <div key={b.appointmentId} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${b.status === 'completed' ? 'bg-success/10' : b.status === 'cancelled' ? 'bg-danger/10' : 'bg-primary/10'}`}>
                            {b.status === 'completed' ? <CheckCircle2 size={16} className="text-success" /> : b.status === 'cancelled' ? <XCircle size={16} className="text-danger" /> : <Clock size={16} className="text-primary" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-foreground truncate">{b.service}</div>
                            <div className="text-xs text-muted-foreground">{b.bookingDate} · {b.location}</div>
                          </div>
                          <StatusBadge status={b.status} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="bg-card border border-border rounded-2xl p-5 shadow-card">
                  <h3 className="font-semibold text-foreground mb-4">Quick Actions</h3>
                  <div className="space-y-2">
                    {[
                      { label: 'Book Appointment', tab: null as Tab | null, href: '/book-appointment', icon: <CalendarDays size={15} /> },
                      { label: 'Test Results', tab: 'test-results' as Tab, href: null, icon: <TestTube2 size={15} /> },
                      { label: 'Payment Receipts', tab: 'receipts' as Tab, href: null, icon: <Receipt size={15} /> },
                      { label: 'Lab Reports', tab: 'reports' as Tab, href: null, icon: <FlaskConical size={15} /> },
                      { label: 'My Profile', tab: 'profile' as Tab, href: null, icon: <User size={15} /> },
                    ].map((action) =>
                      action.href ? (
                        <Link key={action.label} href={action.href} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-foreground hover:bg-secondary hover:text-primary transition-colors">
                          <span className="text-primary">{action.icon}</span>
                          {action.label}
                          <ChevronRight size={14} className="ml-auto text-muted-foreground" />
                        </Link>
                      ) : (
                        <button key={action.label} onClick={() => action.tab && setActiveTab(action.tab)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-foreground hover:bg-secondary hover:text-primary transition-colors text-left">
                          <span className="text-primary">{action.icon}</span>
                          {action.label}
                          <ChevronRight size={14} className="ml-auto text-muted-foreground" />
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Attention Banner */}
              {healthMetrics.attentionItems > 0 && (
                <div className="bg-warning/5 border border-warning/20 rounded-2xl p-4 flex items-start gap-3">
                  <AlertCircle size={18} className="text-warning flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <div className="font-semibold text-foreground text-sm">1 lab result needs your attention</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Your Thyroid Panel from June 20 shows elevated TSH. Please consult your doctor.</div>
                  </div>
                  <button onClick={() => setActiveTab('reports')} className="text-xs font-semibold text-warning hover:text-warning/80 transition-colors flex-shrink-0">
                    View Report
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'appointments' && (
            <BookingHistoryTab
              userId={user?.id || ''}
              bookings={bookings}
              loadingBookings={loadingBookings}
              onRefresh={loadBookings}
            />
          )}
          {activeTab === 'test-results' && (
            <TestResultsTab
              testResults={testResults}
              loading={loadingTestResults}
              onRefresh={loadTestResults}
            />
          )}
          {activeTab === 'receipts' && (
            <PaymentReceiptsTab
              receipts={paymentReceipts}
              loading={loadingReceipts}
              onRefresh={loadReceipts}
            />
          )}
          {activeTab === 'favourites' && (
            <SavedFavouritesTab
              userId={user?.id || ''}
              favourites={favourites}
              loading={loadingFavourites}
              onRefresh={loadFavourites}
            />
          )}
          {activeTab === 'alerts' && (
            <PriceAlertsTab
              userId={user?.id || ''}
              alerts={priceAlerts}
              loading={loadingAlerts}
              onRefresh={loadAlerts}
            />
          )}
          {activeTab === 'reports' && <LabReportsTab />}
          {activeTab === 'health' && <HealthSummaryTab />}
          {activeTab === 'profile' && (
            <ProfileTab user={patientUser} onProfileUpdate={handleProfileUpdate} />
          )}
        </main>
      </div>
    </div>
  );
}
